import { AreaType } from '@/lib/shared-types';
import { api } from '@/lib/api';

/** X-12 — `AreaType` is a Prisma enum from `@/lib/shared-types`, re-exported here. */
export { AreaType };

/** §5 — the spaces hierarchy. A venue cannot trade until this chain exists. */
export interface Branch {
  id: string;
  name: string;
  code: string;
  isActive: boolean;
}

export interface Floor {
  id: string;
  branchId: string;
  name: string;
  level: number;
  width: number;
  height: number;
  isActive: boolean;
  _count?: { areas: number; mapElements: number };
}

export const AREA_TYPES: AreaType[] = Object.values(AreaType);

export interface Area {
  id: string;
  floorId: string;
  name: string;
  type: AreaType;
  capacity: number | null;
  reservable: boolean;
  isActive: boolean;
  tables?: VenueTable[];
  /** `GET /areas` ships these — `AreasService.remove` refuses while either is non-zero. */
  _count?: { tables: number; resources: number };
}

export interface VenueTable {
  id: string;
  areaId: string;
  number: string;
  name: string | null;
  seats: number;
  state: string;
  qrToken: string | null;
  isActive: boolean;
}

/**
 * Both response shapes appear on these endpoints — paginated `{data, meta}` and
 * a bare array — so unwrap once here rather than guessing per screen. Guessing
 * wrong renders an empty list with no error, which is how the waiter floor
 * screen shipped blank.
 */
export function listOf<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload as T[];
  const p = payload as { data?: T[] } | null;
  return Array.isArray(p?.data) ? p.data : [];
}

/** Surfaces the API's own message, including a ValidationPipe's string[]. */
export function apiMessage(error: unknown, fallback: string): string {
  const message = (error as { response?: { data?: { message?: string | string[] } } })?.response
    ?.data?.message;
  if (Array.isArray(message)) return message.join('. ');
  return message ?? fallback;
}

export const setupApi = {
  floors: {
    list: (branchId: string) => api.get('/floors', { params: { branchId, limit: 100 } }),
    create: (body: Record<string, unknown>) => api.post('/floors', body),
    update: (id: string, body: Record<string, unknown>) => api.patch(`/floors/${id}`, body),
    remove: (id: string) => api.delete(`/floors/${id}`),
  },
  areas: {
    list: (floorId: string) => api.get('/areas', { params: { floorId, limit: 100 } }),
    create: (body: Record<string, unknown>) => api.post('/areas', body),
    update: (id: string, body: Record<string, unknown>) => api.patch(`/areas/${id}`, body),
    remove: (id: string) => api.delete(`/areas/${id}`),
  },
  tables: {
    list: (areaId: string) => api.get('/tables', { params: { areaId, limit: 200 } }),
    create: (body: Record<string, unknown>) => api.post('/tables', body),
    update: (id: string, body: Record<string, unknown>) => api.patch(`/tables/${id}`, body),
    remove: (id: string) => api.delete(`/tables/${id}`),
  },
};

/**
 * Just enough of `t` for a helper that only ever interpolates a count. Typed
 * here rather than importing i18next's `TFunction`, whose generic signature
 * drags the whole resource-key union into a file that has no components in it.
 */
type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * What the services actually refuse, in the operator's words.
 *
 * `FloorsService.remove` throws while the floor still has areas and
 * `AreasService.remove` while the area still holds tables or resources, so the
 * button says which one is in the way instead of turning into a 409 toast.
 * Deleting a branch or a table has no such guard — the branch is soft-deleted
 * and the table's sessions, reservations and service requests are detached — so
 * neither is blocked.
 *
 * These live here, beside the shapes they read, because two screens now offer
 * the same delete: the Spaces tab and the setup wizard. A second copy of the
 * rule is how one of the two quietly stops warning, and the warning is the part
 * that turns a refusal into an instruction.
 */
export function floorDeleteBlock(floor: Floor, t: Translate): string | null {
  const areaCount = floor._count?.areas ?? 0;
  return areaCount > 0 ? t('setup.floor.deleteBlocked', { count: areaCount }) : null;
}

export function areaDeleteBlock(area: Area, t: Translate): string | null {
  const held = (area._count?.tables ?? 0) + (area._count?.resources ?? 0);
  return held > 0 ? t('setup.area.deleteBlocked', { count: held }) : null;
}
